import api from '@/services/api';

/**
 * `GET /customers/{id}/policy-prefill/` — this contact, shaped for Add Policy.
 *
 * The server half lives in `backend/apps/customers/prefill.py`; its contract is
 * the same one the lead prefill has always used:
 *
 * - `vehicle_make` / `vehicle_model` / `vehicle_variant` are vehicle-MASTER IDS
 *   where the contact's free text resolved, and `''` where it did not. The
 *   original text rides along in the `_text` twins so the form can show what
 *   was recorded next to a blank select rather than losing it. Nothing is
 *   guessed — a wrong variant reprices the policy.
 * - EVERY vehicle the contact owns comes back, not the first. A contact with
 *   two cars is asked which one; picking silently would write the wrong
 *   vehicle onto a policy and nobody would see it happen.
 *
 * This client lives beside the page rather than in `src/services` because the
 * page is the only consumer and the two halves of the feature are reviewed
 * together.
 */
export interface ContactPrefillVehicle {
  /** `CustomerVehicle` id — the picker's option value, not a policy field. */
  id: string;
  /** Human label for the picker, built from whatever the record actually holds. */
  label: string;
  vehicle_number: string;
  registration_date: string;
  manufacturing_year: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_variant: string;
  vehicle_make_text: string;
  vehicle_model_text: string;
  vehicle_variant_text: string;
  fuel_type_text: string;
  vehicle_type: string;
  ncb_percentage: string;
  cc: string;
  gvw: string;
  /** B-04: the vehicle's own, else the RC record's (masked values blanked).
   *  Optional so an older server's payload still type-checks. */
  engine_number?: string;
  chassis_number?: string;
  /**
   * `contact` = every id above came from this broker's own record for the
   * contact. `registration_trail` = something was filled in from the shared
   * cross-broker registration trail, which can be stale. Shown to the broker
   * rather than hidden.
   */
  source: 'contact' | 'registration_trail' | string;
}

export interface ContactPolicyPrefill {
  customer: string;
  holder: Record<string, string>;
  vehicles: ContactPrefillVehicle[];
}

export async function getContactPolicyPrefill(customerId: string): Promise<ContactPolicyPrefill> {
  const response = await api.get<ContactPolicyPrefill>(`/customers/${customerId}/policy-prefill/`);
  return response.data;
}
